/**
 * Toast Notification Hook
 *
 * Provides access to the global toast notification system.
 * Uses a Zustand store to ensure all toasts are visible regardless of
 * where they're triggered (including inside other hooks).
 *
 * @returns {Object} Toast state and control functions
 * @returns {ToastState} toast - Complete toast state object
 * @returns {Function} showToast - Function to display a toast message
 * @returns {Function} hideToast - Function to manually hide the toast
 *
 * @example
 * ```tsx
 * const { toast, showToast, hideToast } = useToast();
 *
 * // Simple success message (auto-hides after 3 seconds)
 * showToast('Item saved successfully!', 'success');
 *
 * // Error message with custom duration
 * showToast('Failed to save item', 'error', 5000);
 *
 * // Message with action button
 * showToast('Item deleted', 'info', 0, {
 *   label: 'Undo',
 *   onClick: handleUndo
 * });
 *
 * // Render with the shared Toast component
 * <Toast
 *   message={toast.message}
 *   isVisible={toast.isVisible}
 *   type={toast.type}
 *   onClose={hideToast}
 *   action={toast.action}
 *   position={toast.position}
 * />
 * ```
 *
 * Features:
 * - Six toast types: success, error, info, warning, prep (green), return (purple)
 * - Configurable auto-hide duration (default: 3000ms, 0 = no auto-hide)
 * - Optional action buttons with callbacks
 * - Position control (bottom-right or bottom-center)
 * - Global state - toasts triggered anywhere are visible everywhere
 * - Single toast at a time (new toast replaces existing)
 */

import { useToastStore, type ToastState, type ToastType, type ToastAction, type ToastPosition } from '../store/toastStore';

export type { ToastType, ToastPosition, ToastAction };

export const useToast = () => {
  const { message, isVisible, type, duration, action, position, showToast, hideToast } = useToastStore();

  // Return toast state and actions in a format compatible with previous API
  const toast: ToastState = {
    message,
    isVisible,
    type,
    duration,
    action,
    position,
  };

  return { toast, showToast, hideToast };
};
